import {
  emotionStyled,
  styleAtom,
  buttonResetAtom,
} from '@packages/utilities/styled';

import {tokens} from '../../themes';
import {Icon} from '../../icons/Icon';

const prefixSuffixAtom = styleAtom({
  '[data-slot="prefix"], [data-slot="suffix"]': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    /**
     * Adapts to the font-size of the link.
     * Minimum size is 16px.
     *
     * 2023-09-20
     */
    height: 'max(16px, 1em) !important',
    width: 'max(16px, 1em) !important',
    svg: {
      height: 'auto !important',
      width: 'auto !important',
    },
  },
});

/**
 * Some of the variants below use a '&&' for text-decoration declarations. This is to overcome a specificity issue we ran into by compounding the generated classes, e.g. .wf-123.wf-123
 */
const linkAtom = styleAtom(
  {
    cursor: 'pointer',
    fontSize: '$body',
    '&:focus-visible': {
      borderRadius: '$1',
      outline: '1px solid',
      outlineColor: '$blueBorder',
      textDecoration: 'underline !important',
    },
    [`& ${Icon}`]: {
      display: 'inline-block',
      position: 'relative',
      verticalAlign: 'middle',
    },
    [`& ${Icon}:first-of-type:not(:only-child)`]: {
      marginRight: '$0.5',
    },
    [`& ${Icon}:last-of-type:not(:only-child)`]: {
      marginLeft: '$0.5',
    },
  },
  {
    componentName: 'Link',
    variants: {
      colorVariant: {
        danger: {
          color: '$redText',
          '&&': {
            textDecoration: 'none',
          },
          '&:hover': {
            textDecoration: 'underline',
          },
        },
        default: {
          color: '$textPrimary',
          '&&': {
            textDecoration: 'none',
          },
          '&:hover': {
            textDecoration: 'underline',
          },
          '& [data-wf-icon="WebflowIcon"]': {
            color: '$blueText',
          },
        },
        inherit: {
          color: 'currentColor',
          '&&': {
            textDecoration: 'none',
          },
          '&:hover': {
            textDecoration: 'underline',
          },
          '& [data-wf-icon="WebflowIcon"]': {
            color: '$blueText',
          },
        },
        primary: {
          color: '$blueText',
          '& [data-wf-icon="WebflowIcon"]': {
            color: '$blueText',
          },
          '&&': {
            textDecoration: 'none',
          },
          '&:hover': {
            textDecoration: 'underline',
          },
        },
        secondary: {
          color: '$textSecondary',
          '& [data-wf-icon="WebflowIcon"]': {
            color: '$blueText',
          },
          '&&': {
            textDecoration: 'none',
          },
          '&:hover': {
            textDecoration: 'underline',
          },
        },
        success: {
          color: '$greenText',
          '&&': {
            textDecoration: 'none',
          },
          '&:hover': {
            textDecoration: 'underline',
          },
        },
        warning: {
          color: '$yellowText',
          '&&': {
            textDecoration: 'none',
          },
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
      decoration: {
        hide: {
          '&&': {
            textDecoration: 'none',
          },
        },
        show: {
          '&&': {
            textDecoration: 'underline',
          },
        },
      },
      hoverDecoration: {
        hide: {
          '&&': {
            '&:hover': {
              textDecoration: 'none',
            },
          },
        },
        show: {
          '&&': {
            '&:hover': {
              textDecoration: 'underline',
            },
          },
        },
      },
      isDisabled: {
        true: {
          opacity: tokens.opacities.disabled,
          '&&': {
            '&:hover': {
              textDecoration: 'none',
              cursor: 'not-allowed',
            },
          },
        },
      },
      size: {
        medium: {
          // All interactive content in Designer uses `cursor: default`
          cursor: 'default',
        },
      },
      shouldInheritFont: {
        true: {
          fontfamily: 'inherit',
          fontSize: 'inherit',
          fontWeight: 'inherit',
          lineHeight: 'inherit',
        },
      },
    },
    defaultVariants: {
      colorVariant: 'primary',
    },
  }
);

export const LinkView = emotionStyled('a')(
  {},
  {
    composes: [linkAtom, prefixSuffixAtom],
    variants: {
      truncation: {
        truncated: {
          width: '100%',
        },
        'truncated-inline': {
          display: 'inline-block',
          width: '100%',
        },
      },
    },
  }
);

export const ButtonMasqueradingAsLinkView = emotionStyled('span')(
  {},
  {composes: [linkAtom, buttonResetAtom, prefixSuffixAtom]}
);
